/**
 * Minor units in and out of the one input on the budget panel.
 *
 * Every amount crosses the API as an integer number of paise or cents
 * (`app/billing/plans.py`: *"money is not approximate and a float rupee is a
 * rounding error waiting for a reconciliation"*), and the tenant types rupees.
 * These two functions are that boundary, and they are here rather than inside
 * the panel so they can be tested for the cases that actually happen — a comma,
 * a stray ₹, an empty field — without rendering anything.
 *
 * **Formatting is deliberately *not* here.** The API sends `cap_display` and
 * `spend_display` already grouped in the Indian system for INR and the western
 * one for USD, because `app/billing/plans.format_minor` is where that decision
 * was made once. A second implementation in TypeScript would be the hand-written
 * DTO problem in a different costume.
 */

/** What the tenant typed, as minor units. `null` for an empty field. */
export function parseAmount(typed: string): number | null {
  const cleaned = typed.replace(/[\s,₹$]/g, '')
  if (cleaned === '') return null
  const major = Number(cleaned)
  if (!Number.isFinite(major) || major < 0) return null
  return Math.round(major * 100)
}

/** Minor units as the plain number the input shows. Never grouped: it is an
 * input, and a grouped value cannot be edited from the middle without fighting
 * the caret. */
export function toAmountField(minor: number | null | undefined): string {
  if (minor === null || minor === undefined) return ''
  return String(minor / 100)
}

/**
 * Minor units as the grouped figure `shell.html`'s budget panel draws in its
 * input — *8,000* (parity P-BUDG-01, 2026-09-13). Indian grouping for rupees,
 * western for anything else, which is the split `format_minor` makes on the
 * server; applied when the field is seeded and again when it loses focus, so
 * the caret is never fought mid-edit. Whole units unless there are paise.
 */
export function toGroupedAmountField(minor: number | null | undefined, currency: string): string {
  if (minor === null || minor === undefined) return ''
  return (minor / 100).toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US', {
    maximumFractionDigits: 2,
  })
}

/** Whether what was typed is a usable amount, for the submit button's state. */
export function isAmount(typed: string): boolean {
  return parseAmount(typed) !== null
}
